import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';

interface DashboardCardProps {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  fullHeight?: boolean;
}

const CardContainer = styled(motion.div)<{ fullHeight?: boolean }>`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.md};
  border: 1px solid ${theme.colors.gray[200]};
  height: ${({ fullHeight }) => fullHeight ? '100%' : 'auto'};
  
  &:hover {
    box-shadow: ${theme.shadows.lg};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.gray[100]};
`;

const CardTitle = styled.h3`
  color: ${theme.colors.primary};
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  margin: 0;
`;

const CardIcon = styled.div`
  color: ${theme.colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardContent = styled.div`
  color: ${theme.colors.gray[700]};
`;

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  children,
  icon,
  className,
  fullHeight = false
}) => {
  return (
    <CardContainer
      className={className}
      fullHeight={fullHeight}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <CardHeader>
        {icon && <CardIcon>{icon}</CardIcon>}
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {children}
      </CardContent>
    </CardContainer>
  );
};